import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  Tabs,
  Tab,
  Chip,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BasePage from './BasePage';
import { useBusiness } from '../contexts/BusinessContext';
import { useAuth } from '../contexts/AuthContext';
import ReferralsTable from '../components/business/ReferralsTable';
import AddReferralForm from '../components/business/AddReferralForm';
import { ReferralWithMetadata, ReferralFormData } from '../types/business';
import { useReferralManagement } from '../hooks/useReferralManagement';

const ReferralsPage = () => {
  const { business } = useBusiness();
  const { user } = useAuth();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);
  const [addReferralModalOpen, setAddReferralModalOpen] = useState(false);
  const [editReferralModalOpen, setEditReferralModalOpen] = useState(false);
  const [editingReferral, setEditingReferral] = useState<ReferralWithMetadata | null>(null);

  // Determine user type based on route path
  const isBusinessAdmin = location.pathname.includes('/business/admin/');
  
  // Use the centralized referral management hook
  const {
    referrals,
    isLoadingReferrals,
    referralsError,
    addReferral,
    updateReferral,
    deleteReferral,
    isAddingReferral,
    isUpdatingReferral,
    isDeletingReferral
  } = useReferralManagement({
    businessId: business?.id,
    userType: isBusinessAdmin ? 'business' : 'agent',
    userEmail: user?.email
  });

  // Filter referrals based on selected tab
  const getFilteredReferrals = () => {
    switch (activeTab) {
      case 0: // All
        return referrals;
      case 1: // Business Added
        return referrals.filter(r => r.addedBy.type === 'business');
      case 2: // Agent Added
        return referrals.filter(r => r.addedBy.type === 'agent');
      case 3: // My Referrals (for agents)
        return isBusinessAdmin ? [] : referrals.filter(
          r => r.addedBy.type === 'agent' && r.addedBy.email === user?.email
        );
      default:
        return referrals;
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleAddReferralOpen = () => {
    setAddReferralModalOpen(true);
  };

  const handleAddReferralClose = () => {
    setAddReferralModalOpen(false);
  };

  const handleEditReferralOpen = (referral: ReferralWithMetadata) => {
    setEditingReferral(referral);
    setEditReferralModalOpen(true);
  };

  const handleEditReferralClose = () => {
    setEditReferralModalOpen(false);
    setEditingReferral(null);
  };

  const handleAddReferralSubmit = async (referralData: ReferralFormData) => {
    return await addReferral(referralData);
  };

  const handleEditReferralSubmit = async (
    referralId: string,
    updates: Partial<ReferralFormData>
  ) => {
    return await updateReferral(referralId, updates);
  };

  const handleDeleteReferral = async (referralId: string) => {
    return await deleteReferral(referralId);
  };

  const filteredReferrals = getFilteredReferrals();
  const businessReferrals = referrals.filter(r => r.addedBy.type === 'business');
  const agentReferrals   = referrals.filter(r => r.addedBy.type === 'agent');
  const myReferrals      = isBusinessAdmin ? [] : referrals.filter(
    r => r.addedBy.type === 'agent' && r.addedBy.email === user?.email
  );

  return (
    <BasePage
      title={`Referrals Management - ${business?.name || 'CribCritic'}`}
      description="Manage professional referrals for your real estate business"
      keywords="referrals, contractors, professionals, business management"
    >
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Professional Referrals
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage trusted professionals and contractors for your clients
            </Typography>
            {business && (
              <Chip 
                label={`${business.name} ${isBusinessAdmin ? 'Admin' : 'Agent'}`} 
                color="primary" 
                size="small" 
                sx={{ mt: 1 }} 
              />
            )}
          </Box>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={handleAddReferralOpen}
            sx={{ borderRadius: 2 }}
          >
            Add Referral
          </Button>
        </Box>

        {/* Info Alert */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {isBusinessAdmin 
              ? "As a business admin, you can manage all referrals. Business referrals are available to all agents, while agent-specific referrals are only visible to their clients."
              : "You can add referrals for your clients. Business referrals (added by admins) are shared across all agents."
            }
          </Typography>
        </Alert>

        {/* Summary Cards */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Referrals Overview
          </Typography>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" fontWeight="bold" color="primary.main">
                {referrals.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Referrals
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" fontWeight="bold" color="success.main">
                {businessReferrals.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Business Referrals
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" fontWeight="bold" color="info.main">
                {agentReferrals.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Agent Referrals
              </Typography>
            </Box>
            {!isBusinessAdmin && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h3" fontWeight="bold" color="warning.main">
                  {myReferrals.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  My Referrals
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Tab Navigation */}
        <Box sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="referrals tabs">
            <Tab label={`All (${referrals.length})`} />
            <Tab label={`Business (${businessReferrals.length})`} />
            <Tab label={`Agent Added (${agentReferrals.length})`} />
            {!isBusinessAdmin && (
              <Tab label={`My Referrals (${myReferrals.length})`} />
            )}
          </Tabs>
        </Box>

        {/* Referrals Table */}
        <ReferralsTable
          referrals={filteredReferrals}
          isLoading={isLoadingReferrals}
          error={referralsError}
          onEditReferral={handleEditReferralOpen}
          onDeleteReferral={handleDeleteReferral}
          isDeleting={isDeletingReferral}
          userRole={isBusinessAdmin ? 'admin' : 'agent'}
          currentUserEmail={user?.email}
        />
      </Container>

      {/* Add Referral Modal */}
      <AddReferralForm
        open={addReferralModalOpen}
        onClose={handleAddReferralClose}
        onSubmit={handleAddReferralSubmit}
        isSubmitting={isAddingReferral}
        userType={isBusinessAdmin ? 'business' : 'agent'}
        userName={user?.name || ''}
        userEmail={user?.email || ''}
      />

      {/* Edit Referral Modal */}
      <AddReferralForm
        open={editReferralModalOpen}
        onClose={handleEditReferralClose}
        onSubmit={handleAddReferralSubmit} // Not used in edit mode
        onUpdate={handleEditReferralSubmit}
        isSubmitting={isUpdatingReferral}
        isEdit={true}
        editReferral={editingReferral || undefined}
        userType={isBusinessAdmin ? 'business' : 'agent'}
        userName={user?.name || ''}
        userEmail={user?.email || ''}
      />
    </BasePage>
  );
};

export default ReferralsPage;